/**
 * Inactivation rule (personservice, feature 003 F4 + 004 F4).
 *
 * Mirrors `InactivationRuleViewModels.cs` 1:1. Enums travel by number, except
 * `InactivationConditionKey` and `InactivationProtectionKey`, which the
 * backend serializes by NAME (`JsonStringEnumConverter`) — so they are string
 * unions here, not numeric enums.
 *
 * `*Utc` fields are instants. The `DateInterceptor` turns them into the
 * account's wall time on the way in and back into UTC on the way out, so a
 * value read from a response can be sent back untouched (e.g. the preview's
 * `ComputedOnUtc`).
 */

/** `InactivationRuleMode`: how the enabled conditions combine. */
export enum EInactivationRuleMode {
  /** "Todas": every enabled condition must be met. */
  All = 1,
  /** "Pelo menos uma": one enabled condition is enough. */
  Any = 2,
}

/**
 * `CrmAutoDistribution`: who gets the CRM action of each inactivated person.
 * `User` = always the one consultant; `RoundRobin` = in turn among them.
 */
export enum ECrmAutoDistribution {
  User = 1,
  RoundRobin = 2,
}

/** `InactivationRuleSource`: where a rule version came from. */
export enum EInactivationRuleSource {
  User = 1,
  Migration = 2,
  Review = 3,
}

/** `InactivationOutcome`: what the rule says about one person. */
export enum EInactivationOutcome {
  NotMet = 0,
  WouldInactivate = 1,
  Protected = 2,
}

/**
 * `InactivationConditionKey`. A string enum because the backend sends it by
 * name (`JsonStringEnumConverter`); the member names are the C# ones.
 */
export enum EInactivationConditionKey {
  SubscriptionEnded = 'SubscriptionEnded',
  NoAccess = 'NoAccess',
  NoPayment = 'NoPayment',
}

/** `InactivationProtectionKey`, by name, in precedence order. */
export enum EInactivationProtectionKey {
  Active = 'Active',
  Paused = 'Paused',
  RenewalScheduled = 'RenewalScheduled',
  Recurring = 'Recurring',
  Aggregator = 'Aggregator',
}

/**
 * Stable keys of the preview groups. The readable names are the front's
 * (handoff §2); the backend only sends the key, and only for groups with
 * someone in them.
 */
export type TInactivationPreviewGroupKey =
  | 'conditions-met'
  | 'never-accessed-or-subscribed'
  | 'protected-active'
  | 'protected-paused'
  | 'protected-renewal-scheduled'
  | 'protected-recurring'
  | 'protected-aggregator';

/** Smallest and largest number of days a condition accepts (backend `Validate`). */
export const INACTIVATION_RULE_MIN_DAYS = 1;
export const INACTIVATION_RULE_MAX_DAYS = 3650;

/** `Guid.Empty`: the backend refuses it as a funnel, a step or a consultant. */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** Largest `Top` of `Preview/Persons` (`InactivationPreviewPersonsRequest.MaxTop`). */
export const INACTIVATION_PREVIEW_PERSONS_MAX_TOP = 1000;

/**
 * `InactivationCrmAutoViewModel`: every person the daily check inactivates
 * becomes an action in this funnel and step. The backend answers `null` when
 * it is off, so the front sends `null` for off too — never
 * `{ IsEnabled: false, … }`, which would read as a change.
 *
 * With `IsEnabled`, `Save` and `Review` check the shape (see
 * {@link isValidInactivationCrmAuto}) and then ask the CRM whether the funnel,
 * the step and the consultants exist: a refusal is a 422 ("Envio para o CRM:
 * …"), no answer in 10 s a 503. `Preview` checks nothing of it.
 */
export type TInactivationCrmAuto = {
  IsEnabled: boolean;
  FunnelId: string | null;
  StepId: string | null;
  Distribution: ECrmAutoDistribution | null;
  /** `User`: exactly one. `RoundRobin`: one or more, no repeats. */
  ConsultantIds: string[];
};

/**
 * The editable part of a rule — the body of `POST /InactivationRule`,
 * `POST /InactivationRule/Preview` and `POST /InactivationRule/Review`.
 *
 * The backend refuses unknown members (`JsonUnmappedMemberHandling.Disallow`,
 * 422), so the body carries exactly these seven fields. `null` days = condition
 * switched off.
 */
export type TInactivationRuleInput = {
  IsEnabled: boolean;
  Mode: EInactivationRuleMode;
  SubscriptionEndedDays: number | null;
  NoAccessDays: number | null;
  NoPaymentDays: number | null;
  AggregatorException: boolean;
  /**
   * `null` = no CRM send. Optional only because the backend reads a missing
   * member as `null`; {@link toInactivationRuleInput} always writes it.
   */
  CrmAuto?: TInactivationCrmAuto | null;
};

/**
 * `InactivationRuleViewModel` as the backend answers it: one version.
 * `Version = 0` means the tenant never saved a rule (the rule is off).
 */
export type TInactivationRule = TInactivationRuleInput & {
  Version: number;
  InactivationRuleVersionId: string | null;
  Source: EInactivationRuleSource | null;
  SavedBy: string | null;
  SavedByName: string | null;
  SavedOnUtc: string | null;
  /**
   * Hour (0–23) of the daily check, in Brasília's clock. Answer only: the
   * backend ignores it in a body, and {@link toInactivationRuleInput} drops
   * it. `null`/absent when the backend did not say (the review's `Suggested`).
   */
  RoutineRunsAtHour?: number | null;
};

/** `InactivationPreviewGroupViewModel`. */
export type TInactivationPreviewGroup = {
  Key: TInactivationPreviewGroupKey;
  Outcome: EInactivationOutcome;
  Count: number;
  /** Conditions met by someone in the group, in display order. */
  ConditionKeys: EInactivationConditionKey[];
  ProtectionKey: EInactivationProtectionKey | null;
};

/** `InactivationPreviewViewModel`: totals and groups of an unsaved rule. */
export type TInactivationPreview = {
  WouldInactivate: number;
  /** "Sem acesso, mas continuam ativos": the sum of the protected groups. */
  ProtectedWithoutAccess: number;
  Groups: TInactivationPreviewGroup[];
  ComputedOnUtc: string;
};

/**
 * `InactivationPreviewPersonsRequest`. Scope: a group (`GroupKey`), an
 * outcome (`Outcome`, no group) or everyone (neither).
 */
export type TInactivationPreviewPersonsRequest = {
  Rule: TInactivationRuleInput;
  /** Empty = no group filter. */
  GroupKey: TInactivationPreviewGroupKey | '';
  /** Without a group: only `WouldInactivate`, only `Protected`, or `null` for both. */
  Outcome: EInactivationOutcome | null;
  /** The simulation the screen showed; a newer one answers with `Recomputed`. */
  ComputedOnUtc: string | null;
  Skip: number;
  Top: number;
};

/** The scope a feature asks for: a group, an outcome, or everyone. */
export type TInactivationPreviewScope = {
  GroupKey?: TInactivationPreviewGroupKey | '';
  Outcome?: EInactivationOutcome | null;
};

/** `InactivationPreviewConditionViewModel`: a met condition with the measured data. */
export type TInactivationPreviewCondition = {
  Key: EInactivationConditionKey;
  Days: number;
  /** Date of the last measured event (access, payment, subscription end). */
  MeasuredOnUtc: string | null;
  /** When the event never happened: since when the condition was counted. */
  NeverSinceUtc: string | null;
};

/** `InactivationPreviewPlanViewModel`. */
export type TInactivationPreviewPlan = {
  Name: string | null;
  /**
   * Protected by the subscription: `Active`, `Paused`, `RenewalScheduled`,
   * `Recurring`. Would be inactivated: `Finished`, `Closed`, `Transferred`,
   * `DependentRemoved`.
   */
  Situation: string | null;
  /** Effective end, on the last plan of who would be inactivated. */
  EndOnUtc: string | null;
};

/** `InactivationPreviewPersonDetail`. */
export type TInactivationPreviewPersonDetail = {
  /** The plan that protects the person, when the subscription protects. */
  SubscriptionName: string | null;
  /** Days since the last access; `null` = never accessed. */
  DaysWithoutAccess: number | null;
};

/** `InactivationPreviewPersonViewModel`: one person of the preview. */
export type TInactivationPreviewPerson = {
  PersonId: string;
  Name: string | null;
  AvatarUrl: string | null;
  PersonCode: number | null;
  LastAccessOnUtc: string | null;
  Detail: TInactivationPreviewPersonDetail;
  Outcome: EInactivationOutcome;
  GroupKey: TInactivationPreviewGroupKey;
  ProtectionKey: EInactivationProtectionKey | null;
  /** "Entrou por": the met conditions, with the measured data. */
  MetConditions: TInactivationPreviewCondition[];
  /** `null`: never had a plan, or protected by the aggregator. */
  Plan: TInactivationPreviewPlan | null;
  /** The subscriptionservice did not answer: a `null` plan means "unknown". */
  PlanUnavailable: boolean;
};

/** `InactivationPreviewPersonsViewModel`: one page of people. */
export type TInactivationPreviewPersons = {
  Total: number;
  Items: TInactivationPreviewPerson[];
  /** The simulation the rows came from; exports stamp "Simulação" with it. */
  ComputedOnUtc: string;
  /** The request cited an expired simulation: the numbers may differ from the screen. */
  Recomputed: boolean;
};

/** `InactivationRuleReviewViewModel`: the cutover review, until an admin confirms it. */
export type TInactivationRuleReview = {
  Pending: boolean;
  Current: TInactivationRule;
  /** The suggested rule. Only the editable fields are filled. */
  Suggested: TInactivationRule;
  ReviewedOnUtc: string | null;
  ReviewedBy: string | null;
};

/**
 * The editable fields of a rule, and nothing else — what the backend accepts
 * as a body. Picks fields; it does not rename or convert anything. A missing
 * `CrmAuto` is written as `null`, which is how the backend reads it.
 */
export function toInactivationRuleInput(
  rule: TInactivationRuleInput
): TInactivationRuleInput {
  return {
    IsEnabled: rule.IsEnabled,
    Mode: rule.Mode,
    SubscriptionEndedDays: rule.SubscriptionEndedDays,
    NoAccessDays: rule.NoAccessDays,
    NoPaymentDays: rule.NoPaymentDays,
    AggregatorException: rule.AggregatorException,
    CrmAuto: rule.CrmAuto ?? null,
  };
}

/** Whether the rule has at least one condition switched on. */
export function hasInactivationCondition(
  rule: TInactivationRuleInput
): boolean {
  return (
    rule.SubscriptionEndedDays !== null ||
    rule.NoAccessDays !== null ||
    rule.NoPaymentDays !== null
  );
}

/**
 * Mirror of the backend `Validate`: a known mode, every day count `null` or an
 * integer from 1 to 3650, and at least one condition when the rule is on.
 * A rule that fails this gets a 422 from both `Preview` and `POST`.
 */
export function isValidInactivationRule(rule: TInactivationRuleInput): boolean {
  const validDays = (days: number | null): boolean =>
    days === null ||
    (Number.isInteger(days) &&
      days >= INACTIVATION_RULE_MIN_DAYS &&
      days <= INACTIVATION_RULE_MAX_DAYS);

  return (
    (rule.Mode === EInactivationRuleMode.All ||
      rule.Mode === EInactivationRuleMode.Any) &&
    validDays(rule.SubscriptionEndedDays) &&
    validDays(rule.NoAccessDays) &&
    validDays(rule.NoPaymentDays) &&
    (!rule.IsEnabled || hasInactivationCondition(rule))
  );
}

/**
 * Mirror of the backend `ValidateCrmAutoShape`: off (`null` or not enabled)
 * passes; on needs a funnel, a step, a known distribution and at least one
 * consultant, no repeats — exactly one for `User`. A block that fails this
 * gets a 422 from `POST` and `Review` (not from `Preview`).
 */
export function isValidInactivationCrmAuto(
  crmAuto: TInactivationCrmAuto | null | undefined
): boolean {
  if (!crmAuto?.IsEnabled) {
    return true;
  }
  const ids = crmAuto.ConsultantIds;
  return (
    isFilledGuid(crmAuto.FunnelId) &&
    isFilledGuid(crmAuto.StepId) &&
    (crmAuto.Distribution === ECrmAutoDistribution.User ||
      crmAuto.Distribution === ECrmAutoDistribution.RoundRobin) &&
    ids.length > 0 &&
    ids.every(isFilledGuid) &&
    new Set(ids).size === ids.length &&
    (crmAuto.Distribution !== ECrmAutoDistribution.User || ids.length === 1)
  );
}

/** A `Guid` the backend takes as chosen: present and not `Guid.Empty`. */
function isFilledGuid(id: string | null): boolean {
  return !!id && id !== EMPTY_GUID;
}

/**
 * Same CRM send. Off is off however it is written (`null`, absent or not
 * enabled); on compares every field, the consultants in order — the order is
 * the round robin's.
 */
export function isSameInactivationCrmAuto(
  a: TInactivationCrmAuto | null | undefined,
  b: TInactivationCrmAuto | null | undefined
): boolean {
  const on = (crmAuto: TInactivationCrmAuto | null | undefined) =>
    crmAuto?.IsEnabled ? crmAuto : null;
  const x = on(a);
  const y = on(b);
  if (!x || !y) {
    return x === y;
  }
  return (
    x.FunnelId === y.FunnelId &&
    x.StepId === y.StepId &&
    x.Distribution === y.Distribution &&
    x.ConsultantIds.length === y.ConsultantIds.length &&
    x.ConsultantIds.every((id, index) => id === y.ConsultantIds[index])
  );
}

/**
 * Same six criteria fields, same values. `CrmAuto` is left out on purpose:
 * it does not change who would be inactivated, so the preview is not
 * recomputed for it — compare it with {@link isSameInactivationCrmAuto}.
 */
export function isSameInactivationRule(
  a: TInactivationRuleInput | null,
  b: TInactivationRuleInput | null
): boolean {
  if (!a || !b) {
    return a === b;
  }
  return (
    a.IsEnabled === b.IsEnabled &&
    a.Mode === b.Mode &&
    a.SubscriptionEndedDays === b.SubscriptionEndedDays &&
    a.NoAccessDays === b.NoAccessDays &&
    a.NoPaymentDays === b.NoPaymentDays &&
    a.AggregatorException === b.AggregatorException
  );
}
